import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';

import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import {
  setChatContext,
  selectAssistantsList,
  selectActiveAssistant,
  startChatWithAssistant,
  sendMessage,
  setCurrentUser,
  selectSendingMessage,
  selectMessagesFromActiveChat,
  IMessage,
  resetChat,
  selectLoading,
  IAssistant,
  selectContext,
  Context,
} from '@telespot/chat/data-access';
import { User } from '@telespot/sdk';

import { AuthService } from '@telespot/web-core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { currentCaseId } from '@telespot/reports/data-access';

@Component({
    selector: 'ts-chat-box',
    templateUrl: './chat-box.component.html',
    styleUrls: ['./chat-box.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ChatBoxComponent implements OnDestroy, OnInit {
  @Output() showChatBoxChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

  public readonly assistants$ = this.store.select(selectAssistantsList);
  public readonly activeAssistant$ = this.store.select(selectActiveAssistant);
  public readonly currentCaseId$ = this.store.select(currentCaseId);
  public readonly sending$ = this.store.select(selectSendingMessage);
  public readonly currentUser$ = this.authService.currentUser$;
  public readonly messages$ = this.store.select(selectMessagesFromActiveChat);
  public readonly loading$ = this.store.select(selectLoading);
  public readonly context$ = this.store.select(selectContext);
  public readonly currentOrganization$ = this.authService.currentOrganization$;

  public activeAssistant: IAssistant;
  public chatVisible = false;
  public isLoggedIn = false;
  public isChatMinimized = false;
  public llmFeatureEnabled = false;
  public newMessage = '';
  public messages: IMessage[] = [];
  public currentContext: Context;

  private _destroy$ = new Subject<void>();
  private _startX: number;
  private _startY: number;
  private resizing = false;

  constructor(
    private router: Router,
    private store: Store,
    private authService: AuthService,
    private languageService: TranslateService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.store.dispatch(setCurrentUser({ user: this.authService.currentUser }));
  }

  ngOnInit(): void {
    this.currentOrganization$
      .pipe(
        takeUntil(this._destroy$),
        filter((organization) => !!organization)
      )
      .subscribe((organization) => {
        const llmChatFeature = organization.license?.features?.llm_chat;
        this.llmFeatureEnabled =
          (typeof llmChatFeature === 'object' ? llmChatFeature.enabled : llmChatFeature) ?? false;
        this.chatVisible = this.llmFeatureEnabled;
        this.changeDetectorRef.markForCheck();
      });

    this.router.events
      .pipe(
        takeUntil(this._destroy$),
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        this.chatVisible = this.llmFeatureEnabled && !this.router.url.includes('login');
        this.changeDetectorRef.markForCheck();
      });

    this.currentCaseId$.pipe(takeUntil(this._destroy$)).subscribe((caseId) => {
      this.store.dispatch(setChatContext({ caseId }));
    });

    this.activeAssistant$.pipe(takeUntil(this._destroy$)).subscribe((activeAssistant) => {
      this.activeAssistant = activeAssistant;
    });

    this.currentUser$.pipe(takeUntil(this._destroy$)).subscribe((user) => {
      this.isLoggedIn = !!user;
    });

    this.messages$.pipe(takeUntil(this._destroy$)).subscribe((messages) => {
      this.messages = messages;
    });

    this.context$.pipe(takeUntil(this._destroy$)).subscribe((ctx) => {
      this.currentContext = ctx;
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
  }

  public closeChat() {
    this.showChatBoxChanged.emit(false);
  }
  public reset() {
    this.store.dispatch(resetChat());
  }

  public selectAssistant(assistantId: string) {
    this.store.dispatch(startChatWithAssistant({ assistantId }));
  }

  public sendMessage() {
    this.store.dispatch(sendMessage({ content: this.newMessage, timestamp: Date.now() }));
    this.newMessage = '';
  }

  public getSenderRole(sender: User | IAssistant) {
    return sender instanceof User;
  }

  public startResize(event: MouseEvent): void {
    if (!this.isChatMinimized) {
      this._startX = event.clientX;
      this._startY = event.clientY;
      this.resizing = true;
      document.body.style.cursor = 'col-resize';
      document.addEventListener('mousemove', this.resize.bind(this));
      document.addEventListener('mouseup', this.stopResize.bind(this));
    }
  }

  public getAssistantDescription(description: any) {
    return description[this.languageService.currentLang || 'en'];
  }

  public toggleChat(): void {
    this.isChatMinimized = !this.isChatMinimized;
    if (this.isChatMinimized) {
      const cardContainer = document.querySelector('.cardContainer') as HTMLElement;
      cardContainer.style.height = 'auto';
      cardContainer.style.overflow = 'hidden';
    } else {
      const cardContainer = document.querySelector('.cardContainer') as HTMLElement;
      cardContainer.style.height = '450px';
      cardContainer.style.overflow = 'auto';
    }
  }

  private resize(event: MouseEvent): void {
    if (!this.resizing || this.isChatMinimized) {
      return;
    }

    const deltaX = event.clientX - this._startX;
    const deltaY = event.clientY - this._startY;
    const newWidth = Math.max(300 - deltaX, 300);
    const newHeight = Math.max(450 - deltaY, 450);

    const cardContainer = document.querySelector('.cardContainer') as HTMLElement;
    cardContainer.style.width = newWidth + 'px';
    cardContainer.style.height = newHeight + 'px';
  }

  private stopResize(): void {
    if (this.resizing) {
      this.resizing = false;
      document.body.style.cursor = 'auto';
      document.removeEventListener('mousemove', this.resize.bind(this));
      document.removeEventListener('mouseup', this.stopResize.bind(this));
    }
  }
}
